import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  trackEvent, 
  identifyUser, 
  trackPageView, 
  trackUserAction, 
  trackError,
  mixpanelService 
} from '../services/mixpanel';

interface UseMixpanelOptions {
  trackPageViews?: boolean;
  trackErrors?: boolean;
  userEmail?: string;
}

export const useMixpanel = (options: UseMixpanelOptions = {}) => {
  const location = useLocation();
  const { trackPageViews = true, trackErrors = true, userEmail } = options;

  useEffect(() => {
    if (trackPageViews) {
      trackPageView(location.pathname, {
        path: location.pathname,
        search: location.search,
        hash: location.hash
      }, userEmail);
    }
  }, [location, trackPageViews, userEmail]);

  useEffect(() => {
    if (!trackErrors) return;

    const handleError = (event: ErrorEvent) => {
      trackError(new Error(event.message), {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error_type: 'javascript_error'
      }, userEmail);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      trackError(new Error(event.reason), {
        error_type: 'unhandled_promise_rejection'
      }, userEmail);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [trackErrors, userEmail]);

  const track = useCallback((event: string, properties?: Record<string, any>) => {
    trackEvent(event, properties, userEmail);
  }, [userEmail]);

  const identify = useCallback((userId: string, properties?: Record<string, any>) => {
    identifyUser(userId, properties, userEmail);
  }, [userEmail]);

  const trackAction = useCallback((action: string, properties?: Record<string, any>) => {
    trackUserAction(action, properties, userEmail);
  }, [userEmail]);

  const trackPage = useCallback((pageName: string, properties?: Record<string, any>) => {
    trackPageView(pageName, properties, userEmail);
  }, [userEmail]);

  const trackErrorEvent = useCallback((error: Error, context?: Record<string, any>) => {
    trackError(error, context, userEmail);
  }, [userEmail]);

  const setUserProperties = useCallback((properties: Record<string, any>) => {
    mixpanelService.setUserProperties(properties, userEmail);
  }, [userEmail]);

  return {
    track,
    identify,
    trackAction,
    trackPage,
    trackError: trackErrorEvent,
    setUserProperties
  };
};
